import { IdNamePair, NavigationItemDto } from '../types/types';
import React, { useCallback, useEffect, useState } from 'react';
import { useUserContext } from '../contexts/AuthProvider';
import { useLocation } from 'react-router-dom';
import {
  constructSearchQueryFromFilters,
  getDefaultFilters,
  getSelectedFilters,
  readFilterTagsFromQuery,
} from '../utils/helpers';
import { Observable } from 'windowed-observable';

const filtersObservable = new Observable('navigation-filters');

export const useNavigationFilters = (
  history: any,
  filters: NavigationItemDto[],
  filterNames: string[],
  limitedAccess: boolean,
  setFilterNames: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const [selectedFilters, setSelectedFilters] = useState<IdNamePair[]>();
  const [storageSearchParams, setStorageSearchParams] = useState<string | null>();
  const [errorInQueryParams, setErrorInQueryParams] = useState<boolean | null>();
  const { navData } = useUserContext();
  const location = useLocation();

  // Set storageSearchParams when dependencies change.
  useEffect(() => {
    if (limitedAccess) return;
    if (readFilterTagsFromQuery(location.search).length > 0) return;
    setStorageSearchParams(localStorage.getItem('initial-dashboard-filters'));
  }, [limitedAccess, location.search]);

  // Publish selectedFilters when dependencies change.
  useEffect(() => {
    if (storageSearchParams) {
      const filterTags = readFilterTagsFromQuery(storageSearchParams);
      setSelectedFilters(getSelectedFilters(navData, filterTags));
    }
  }, [navData, storageSearchParams]);

  // Replace history when dependencies change.
  useEffect(() => {
    if (storageSearchParams) {
      location.search = storageSearchParams;
      history.replace({ ...location });
    }
  }, [history, location, storageSearchParams]);

  // Publish to observable when selectedFilters change
  useEffect(() => {
    filtersObservable.publish(selectedFilters);
  }, [selectedFilters]);

  // This needs to be combined & tested with filters from cookie useEffect
  useEffect(() => {
    const filterTags = readFilterTagsFromQuery(location.search);
    if (filterTags.length === 0) {
      const defaultFilters = getDefaultFilters(filters, filterNames);
      if (defaultFilters.selectedOptions && defaultFilters.selectedOptions.length > 0) {
        location.search = constructSearchQueryFromFilters(defaultFilters.selectedOptions);
        history.replace({ ...location });
      }
      setSelectedFilters(Array.from(new Set(defaultFilters.data)));
      if (defaultFilters.data.length == 0) {
        setErrorInQueryParams(true);
      }
    } else {
      const selectedFiltersFromUrl = getSelectedFilters(filters, filterTags);
      const isIncorrectUrl = selectedFiltersFromUrl.some((itm) => itm.name == '');
      setSelectedFilters(selectedFiltersFromUrl);
      setErrorInQueryParams(isIncorrectUrl);
    }
  }, [filterNames, filters, history, location]);

  // Replace browser history, change filter names for different client
  const selectedFiltersChanged = useCallback(
    (selectedFilters: string[]) => {
      if (selectedFilters && selectedFilters.length > 0) {
        location.search = constructSearchQueryFromFilters(selectedFilters);
        history.replace({ ...location });

        // Each client can have different filter names, set those when client changed
        const client = filters.find((x) => x.name === selectedFilters[0]);
        if (client) {
          setFilterNames(client.filters);
        }
        setSelectedFilters(getSelectedFilters(filters, selectedFilters));
      }
    },
    [location, history, filters, setFilterNames]
  );

  return { selectedFiltersChanged, errorInQueryParams };
};
